import React, { useState, useEffect } from 'react';
import { Bell, X, Loader2, BarChart as BarChartIcon, Cloud, FileDown } from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from 'recharts';
import _ from 'lodash';
import { jsPDF } from 'jspdf';

import { simulationService } from '../../services/simulationService';
import { analyticsService } from '../../services/analyticsService';
import { Card } from '../ui/card';
import Calculator from '../simulation/Calculator';
import MaterialSpecificationUploader from '../simulation/MaterialSpecificationUploader';
import { useSidebar } from '../../contexts/SidebarContext';
import { getSchichtType } from '../../utils/schichtType';

const formatDate = (timestamp) => {
  if (!timestamp) return '';
  const date = new Date(timestamp);
  if (isNaN(date.getTime())) return '';
  return `${date.getDate().toString().padStart(2, '0')}.${(date.getMonth() + 1).toString().padStart(2, '0')}.${date.getFullYear()}`;
};
const KeyMetrics = ({ metrics, isLoading, onMetricClick, maxRows, selectedProject, onTransferValues, onMaterialDichteApplied }) => {
  if (isLoading) {
    return (
      <div className="flex gap-4">
        <Card>
          <div className="p-6 flex justify-center items-center" style={{ height: '140px' }}>
            <Loader2 className="h-6 w-6 animate-spin text-green-500" />
          </div>
        </Card>
      </div>
    );
  }

  // Fixed width and height for cards to ensure proper scrolling
  const cardWidth = "w-[280px]"; // Fixed width for all cards in the scrollable area
  const cardHeight = "h-[160px]"; // Fixed height for all cards to ensure consistent layout

  return (
    <div className="flex flex-row gap-4 overflow-y-hidden">
      {metrics.map((metric, index) => (
        <Card
          key={index}
          className={`hover:shadow-md transition-shadow ${cardWidth} overflow-hidden relative`}
        >
          <div className="p-4 overflow-hidden" style={{ height: '140px' }}>
            {/* Upload button in top-right corner */}
            <div className="absolute top-2 right-2">
              <MaterialSpecificationUploader
                materialName={metric.name}
                projectName={selectedProject?.name}
                projectLocation={selectedProject?.location}
                onTransferValues={onTransferValues}
                onMaterialDichteApplied={onMaterialDichteApplied}
                onBeforeUpload={() => onMetricClick(metric)}
              />
            </div>

            {/* Card content - clickable area */}
            <div
              className="cursor-pointer h-full pr-8"
              onClick={() => onMetricClick(metric)}
            >
              <div className="text-sm text-gray-500">
                {metric.name}
              </div>
              <div className="text-2xl font-semibold mt-1">
                {metric.value.toLocaleString('de-DE')} t
              </div>
              {metric.location && (
                <div className="text-sm mt-1 text-gray-600">
                  {metric.location}
                </div>
              )}
              {metric.co2Value !== undefined && (
                <>
                  <div className="h-px bg-gray-200 my-2"></div>
                  <div className="flex items-center gap-1 text-green-500 font-medium">
                    <Cloud className="w-4 h-4 flex-shrink-0" />
                    <span>{metric.co2Value.toFixed(2)} kg CO2e/t</span>
                  </div>
                </>
              )}
            </div>
          </div>
        </Card>
      ))}
    </div>
  );
};

const MaterialChart = ({ materialCO2Values, onRemoveMaterial }) => {
  const colors = {
    A1: 'rgba(75, 192, 119, 0.8)',   // Matching the green from image
    A2: 'rgba(79, 161, 238, 0.8)',   // Matching the blue from image
    A3: 'rgba(165, 135, 246, 0.8)',  // Matching the purple from image
    A4: 'rgba(251, 146, 60, 0.8)',   // Orange for A4 - Transport Baustelle
  };

  // Transform data for the chart
  const transformedData = Object.entries(materialCO2Values).map(([name, data]) => ({
    name,
    A1: data.breakdown.A1 || 0,
    A2: data.breakdown.A2 || 0,
    A3: data.breakdown.A3 || 0,
    A4: data.breakdown.A4 || 0
  }));

  return (
    <div className="flex flex-col h-full">
      <div className="flex justify-between items-center p-4 pb-2">
        <div className="flex items-center">
          <BarChartIcon className="h-5 w-5 text-gray-500 mr-2" />
          <h2 className="text-lg font-semibold">CO2e Vergleich</h2>
        </div>
        <div className="flex">
          {transformedData.map((entry, index) => (
            <button
              key={index}
              className="p-1 hover:bg-gray-100 rounded-full transition-colors ml-2"
              onClick={() => onRemoveMaterial(entry.name)}
            >
              <X className="h-4 w-4 text-gray-500" />
            </button>
          ))}
        </div>
      </div>
      <div className="flex-1 overflow-hidden" style={{ minHeight: '300px' }}>
        <ResponsiveContainer width="100%" height="100%">
          <BarChart
            data={transformedData}
            margin={{ top: 10, right: 30, left: 20, bottom: 20 }}
          >
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="name" />
            <YAxis
              label={{
                value: 'kg CO2e/t',
                angle: -90,
                position: 'insideLeft',
                style: { textAnchor: 'middle' }
              }}
              hide={false}
              axisLine={true}
              tickLine={true}
              allowDecimals={false}
              tickCount={5}
              domain={[0, 'auto']}
              overflow="hidden"
            />
            <Tooltip
              formatter={(value, name) => {
                const labels = {
                  A1: 'A1 - Rohstoffbereitstellung',
                  A2: 'A2 - Transport',
                  A3: 'A3 - Produktion',
                  A4: 'A4 - Transport Baustelle'
                };
                return [`${value.toFixed(2)} kg CO2e/t`, labels[name]];
              }}
              contentStyle={{
                backgroundColor: 'white',
                border: '1px solid #ccc',
                borderRadius: '4px'
              }}
            />
            <Legend
              formatter={(value) => {
                const labels = {
                  A1: 'A1 - Rohstoffbereitstellung',
                  A2: 'A2 - Transport',
                  A3: 'A3 - Produktion',
                  A4: 'A4 - Transport Baustelle'
                };
                return labels[value];
              }}
            />
            <Bar
              dataKey="A1"
              stackId="a"
              fill={colors.A1}
              name="A1"
            />
            <Bar
              dataKey="A2"
              stackId="a"
              fill={colors.A2}
              name="A2"
            />
            <Bar
              dataKey="A3"
              stackId="a"
              fill={colors.A3}
              name="A3"
            />
            <Bar
              dataKey="A4"
              stackId="a"
              fill={colors.A4}
              name="A4"
              radius={[4, 4, 0, 0]}
            />
          </BarChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
};

const ProjectSearchModal = ({ isOpen, onClose, onSelectProject }) => {
  const [searchInput, setSearchInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [projects, setProjects] = useState([]);
  const [filteredProjects, setFilteredProjects] = useState([]);

  useEffect(() => {
    const fetchProjects = async () => {
      try {
        setIsLoading(true);
        const response = await simulationService.getProjects();
        const projectList = response.data;
        setProjects(projectList);
        setFilteredProjects(projectList);
      } catch (error) {
        console.error('Error fetching projects:', error);
      } finally {
        setIsLoading(false);
      }
    };

    if (isOpen) {
      fetchProjects();
    }
  }, [isOpen]);

  useEffect(() => {
    if (searchInput) {
      const filtered = projects.filter(project =>
        project.toLowerCase().includes(searchInput.toLowerCase())
      );
      setFilteredProjects(filtered);
    } else {
      setFilteredProjects(projects);
    }
  }, [searchInput, projects]);

  if (!isOpen) return null;

  const handleProjectClick = async (project) => {
    try {
      const projectData = await simulationService.getProjectData(project);
      onSelectProject({
        name: project,
        timeframe: projectData.timeframe,
        data: projectData.data,
        location: projectData.location
      });
      onClose();
    } catch (error) {
      console.error('Error fetching project data:', error);
      onClose();
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 w-full max-w-md relative">
        <button
          onClick={onClose}
          className="absolute right-4 top-4 text-gray-500 hover:text-gray-700 transition-colors"
        >
          <X className="h-5 w-5" />
        </button>

        <h2 className="text-xl mb-4">Projekt auswählen</h2>

        <div className="space-y-4">
          <div className="relative">
            <input
              type="text"
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              className="w-full p-2 pr-16 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="Projekt Name eingeben"
              autoFocus
            />
            <span className="absolute right-3 top-1/2 -translate-y-1/2 text-sm text-gray-400">
              {filteredProjects.length} Projekte
            </span>
          </div>

          {isLoading ? (
            <div className="flex justify-center py-4">
              <Loader2 className="h-6 w-6 animate-spin text-green-500" />
            </div>
          ) : (
            <div className="max-h-60 overflow-y-auto border border-gray-200 rounded-md">
              {filteredProjects.map((project) => (
                <button
                  key={project}
                  onClick={() => handleProjectClick(project)}
                  className="w-full text-left px-4 py-2 hover:bg-gray-100 focus:bg-gray-100 focus:outline-none transition-colors"
                >
                  {project}
                </button>
              ))}
              {filteredProjects.length === 0 && (
                <div className="px-4 py-2 text-gray-500">
                  Keine Projekte gefunden
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

const fetchSavedCalculationsForMetrics = async (metrics, projectId) => {
  const inputsMap = {};
  const breakdownMap = {};
  const breakdown1990Map = {};
  for (const metric of metrics) {
    try {
      let result;
      if (metric.source === 'calculatedLayers') {
        const layerTypeMap = {
          'Deckschicht': 'deckschichten',
          'Binderschicht': 'binderschichten',
          'Tragschicht 1': 'tragschichten',
          'Tragschicht 2': 'tragschichten'
        };
        const layerType = layerTypeMap[metric.layerName];
        if (layerType) {
          result = await simulationService.getLayerCO2(projectId, layerType);
          if (result.success && result.data?.co2Data) {
            inputsMap[metric.name] = result.data.co2Data.calculationInputs || null;
            if (result.data.co2Data.co2Breakdown) {
              breakdownMap[metric.name] = {
                value: result.data.co2Data.co2Value,
                breakdown: result.data.co2Data.co2Breakdown
              };
            }
            if (result.data.co2Data.co2Breakdown1990) {
              breakdown1990Map[metric.name] = {
                value: result.data.co2Data.co2Value1990,
                breakdown: result.data.co2Data.co2Breakdown1990
              };
            }
          }
        }
      } else if (metric.productionPlant?.location?.postalCode && metric.productionPlant?.location?.city) {
        result = await simulationService.getLastCalculation(
          projectId,
          metric.name,
          { postalCode: metric.productionPlant.location.postalCode, city: metric.productionPlant.location.city }
        );
        if (result.success && result.data) {
          if (result.data.calculationInputs) {
            inputsMap[metric.name] = result.data.calculationInputs;
          }
          if (result.data.breakdown) {
            breakdownMap[metric.name] = {
              value: result.data.co2Value,
              breakdown: result.data.breakdown
            };
          }
          if (result.data.breakdown1990) {
            breakdown1990Map[metric.name] = {
              value: result.data.co2Value1990,
              breakdown: result.data.breakdown1990
            };
          }
        }
      }
    } catch (e) {
      console.log(`Could not fetch data for ${metric.name}:`, e.message);
    }
  }
  return { inputsMap, breakdownMap, breakdown1990Map };
};

// Helper: check page break
const checkPageBreak = (doc, y, needed = 20) => {
  if (y + needed > 280) {
    doc.addPage();
    return 20;
  }
  return y;
};

const renderCalculatorInputs = (doc, y, inputs) => {
  const pageWidth = doc.internal.pageSize.getWidth();
  const col1 = 20;
  const col2 = 100;

  const addRow = (label, value) => {
    y = checkPageBreak(doc, y, 7);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(8);
    doc.text(label, col1, y);
    doc.setFont('helvetica', 'normal');
    doc.text(String(value || '-'), col2, y);
    y += 6;
    return y;
  };

  // Füller <0,063
  y = addRow('Füller <0,063 M.-%', inputs.materialPercentages?.fueller);
  y = addRow('Füller Entfernung (km)', inputs.distances?.fueller);
  y = addRow('Füller Transport', inputs.transportValues?.fueller);

  // Gestein components
  if (inputs.gesteinComponents && inputs.gesteinComponents.length > 0) {
    inputs.gesteinComponents.forEach((comp, idx) => {
      const label = comp.bezeichnung || `Gesteinskörnungen ${idx + 1}`;
      y = addRow(`${label} M.-%`, comp.percentage);
      y = addRow(`${label} Entfernung (km)`, comp.distance);
      y = addRow(`${label} Transport`, comp.transport);
    });
  }

  // Asphaltgranulat
  y = addRow('Asphaltgranulat M.-%', inputs.materialPercentages?.asphalt);
  y = addRow('Asphaltgranulat Entfernung (km)', inputs.distances?.asphalt);
  y = addRow('Asphaltgranulat Transport', inputs.transportValues?.asphalt);

  // Frischbindemittelzugabe
  y = addRow('Frischbindemittelzugabe M.-%', inputs.materialPercentages?.bindemittel);
  y = addRow('Frischbindemittelzugabe Entfernung (km)', inputs.distances?.bindemittel);
  y = addRow('Frischbindemittelzugabe Transport', inputs.transportValues?.bindemittel);

  // Frischbindemittel
  y = addRow('Frischbindemittel', inputs.selectedBindemittel);

  // Herstellungsart
  y = addRow('Herstellungsart', inputs.selectedProductionType);

  // Gesteinskörnungen (inkl. Füller) Lagerung & Brennstoff
  y = addRow('Gesteinskörnungen Lagerung', inputs.storageValues?.gestein);
  y = addRow('Gesteinskörnungen Brennstoff', inputs.fuelValues?.gestein);

  // Asphaltgranulat Lagerung & Brennstoff
  y = addRow('Asphaltgranulat Lagerung', inputs.storageValues?.asphalt);
  y = addRow('Asphaltgranulat Brennstoff', inputs.fuelValues?.asphalt);

  // Transport Baustelle
  y = addRow('Transport Baustelle (km)', inputs.constructionSiteTransport?.distance);
  y = addRow('Transport Baustelle Fahrzeug', inputs.constructionSiteTransport?.vehicle);

  return y;
};

const renderBarChartInPDF = (doc, y, materialCO2Values) => {
  const pageWidth = doc.internal.pageSize.getWidth();
  const entries = Object.entries(materialCO2Values);
  if (entries.length === 0) return y;

  const chartLeft = 30;
  const chartRight = pageWidth - 20;
  const chartWidth = chartRight - chartLeft;
  const chartHeight = 80;
  const chartTop = y;
  const chartBottom = y + chartHeight;

  // Colors matching the app (RGB)
  const colors = {
    A1: [75, 192, 119],
    A2: [79, 161, 238],
    A3: [165, 135, 246],
    A4: [251, 146, 60],
  };

  // Calculate max total for Y-axis scale
  const totals = entries.map(([, data]) => {
    const b = data.breakdown || {};
    return (b.A1 || 0) + (b.A2 || 0) + (b.A3 || 0) + (b.A4 || 0);
  });
  const maxTotal = Math.max(...totals, 1);
  // Round up to a nice number
  const yAxisMax = Math.ceil(maxTotal / 10) * 10 || 10;

  // Draw Y-axis
  doc.setDrawColor(180, 180, 180);
  doc.setLineWidth(0.3);
  doc.line(chartLeft, chartTop, chartLeft, chartBottom);

  // Y-axis ticks and grid lines
  const tickCount = 5;
  doc.setFontSize(7);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(120, 120, 120);
  for (let i = 0; i <= tickCount; i++) {
    const tickY = chartBottom - (i / tickCount) * chartHeight;
    const tickValue = ((i / tickCount) * yAxisMax).toFixed(0);
    doc.text(tickValue, chartLeft - 2, tickY + 1, { align: 'right' });
    if (i > 0) {
      doc.setDrawColor(230, 230, 230);
      doc.setLineDashPattern([1, 1], 0);
      doc.line(chartLeft, tickY, chartRight, tickY);
    }
  }
  doc.setLineDashPattern([], 0);

  // Y-axis label
  doc.setFontSize(7);
  doc.setTextColor(100, 100, 100);
  doc.text('kg CO2e/t', 14, chartTop + chartHeight / 2, { angle: 90 });

  // Draw X-axis
  doc.setDrawColor(180, 180, 180);
  doc.line(chartLeft, chartBottom, chartRight, chartBottom);

  // Draw bars
  const barCount = entries.length;
  const groupWidth = chartWidth / barCount;
  const barWidth = Math.min(groupWidth * 0.5, 30);

  entries.forEach(([name, data], index) => {
    const b = data.breakdown || {};
    const phases = [
      { key: 'A1', val: b.A1 || 0 },
      { key: 'A2', val: b.A2 || 0 },
      { key: 'A3', val: b.A3 || 0 },
      { key: 'A4', val: b.A4 || 0 },
    ];

    const barX = chartLeft + index * groupWidth + (groupWidth - barWidth) / 2;
    let currentBottom = chartBottom;

    phases.forEach(({ key, val }) => {
      if (val <= 0) return;
      const barHeight = (val / yAxisMax) * chartHeight;
      const barY = currentBottom - barHeight;

      const [r, g, b_] = colors[key];
      doc.setFillColor(r, g, b_);
      doc.setDrawColor(255, 255, 255);
      doc.setLineWidth(0.2);
      doc.rect(barX, barY, barWidth, barHeight, 'FD');

      currentBottom = barY;
    });

    // Total label on top of bar
    const total = phases.reduce((sum, p) => sum + p.val, 0);
    const topY = chartBottom - (total / yAxisMax) * chartHeight;
    doc.setFontSize(6);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(60, 60, 60);
    doc.text(total.toFixed(1), barX + barWidth / 2, topY - 2, { align: 'center' });

    // X-axis label (material name) — truncate if too long
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(80, 80, 80);
    const labelX = barX + barWidth / 2;
    const displayName = name.length > 20 ? name.substring(0, 18) + '...' : name;
    doc.text(displayName, labelX, chartBottom + 5, { align: 'center' });
  });

  // Legend
  const legendY = chartBottom + 12;
  const legendItems = [
    { key: 'A1', label: 'A1 - Rohstoffbereitstellung' },
    { key: 'A2', label: 'A2 - Transport' },
    { key: 'A3', label: 'A3 - Produktion' },
    { key: 'A4', label: 'A4 - Transport Baustelle' },
  ];
  let legendX = chartLeft;
  doc.setFontSize(7);
  legendItems.forEach(({ key, label }) => {
    const [r, g, b_] = colors[key];
    doc.setFillColor(r, g, b_);
    doc.rect(legendX, legendY - 2.5, 4, 3, 'F');
    doc.setTextColor(80, 80, 80);
    doc.text(label, legendX + 5.5, legendY);
    legendX += doc.getTextWidth(label) + 10;
  });

  return legendY + 8;
};

const renderComparisonBarChartInPDF = (doc, y, currentValues, values1990) => {
  const pageWidth = doc.internal.pageSize.getWidth();
  // Only render materials that have both current and 1990 data
  const materialNames = Object.keys(currentValues).filter(name => values1990[name]);
  if (materialNames.length === 0) return y;

  const chartLeft = 30;
  const chartRight = pageWidth - 20;
  const chartWidth = chartRight - chartLeft;
  const chartHeight = 80;
  const chartTop = y;
  const chartBottom = y + chartHeight;

  // Colors: current = vivid, 1990 = muted/grey
  const currentColors = { A1: [75, 192, 119], A2: [79, 161, 238], A3: [165, 135, 246], A4: [251, 146, 60] };
  const colors1990 = { A1: [134, 168, 145], A2: [139, 163, 199], A3: [181, 163, 199], A4: [199, 168, 145] };

  // Calculate max total for Y-axis scale
  const allTotals = materialNames.flatMap(name => {
    const cur = currentValues[name]?.breakdown || {};
    const old = values1990[name]?.breakdown || {};
    return [
      (cur.A1 || 0) + (cur.A2 || 0) + (cur.A3 || 0) + (cur.A4 || 0),
      (old.A1 || 0) + (old.A2 || 0) + (old.A3 || 0) + (old.A4 || 0)
    ];
  });
  const yAxisMax = Math.ceil(Math.max(...allTotals, 1) / 10) * 10 || 10;

  // Y-axis
  doc.setDrawColor(180, 180, 180);
  doc.setLineWidth(0.3);
  doc.line(chartLeft, chartTop, chartLeft, chartBottom);

  const tickCount = 5;
  doc.setFontSize(7);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(120, 120, 120);
  for (let i = 0; i <= tickCount; i++) {
    const tickY = chartBottom - (i / tickCount) * chartHeight;
    const tickValue = ((i / tickCount) * yAxisMax).toFixed(0);
    doc.text(tickValue, chartLeft - 2, tickY + 1, { align: 'right' });
    if (i > 0) {
      doc.setDrawColor(230, 230, 230);
      doc.setLineDashPattern([1, 1], 0);
      doc.line(chartLeft, tickY, chartRight, tickY);
    }
  }
  doc.setLineDashPattern([], 0);

  doc.setFontSize(7);
  doc.setTextColor(100, 100, 100);
  doc.text('kg CO2e/t', 14, chartTop + chartHeight / 2, { angle: 90 });

  // X-axis
  doc.setDrawColor(180, 180, 180);
  doc.line(chartLeft, chartBottom, chartRight, chartBottom);

  // Draw paired bars (current + 1990 side by side)
  const groupCount = materialNames.length;
  const groupWidth = chartWidth / groupCount;
  const barWidth = Math.min(groupWidth * 0.3, 20);
  const gap = 2;

  const drawStackedBar = (barX, bWidth, breakdown, colors) => {
    let currentBottom = chartBottom;
    const total = ['A1', 'A2', 'A3', 'A4'].reduce((sum, key) => sum + (breakdown[key] || 0), 0);
    ['A1', 'A2', 'A3', 'A4'].forEach(key => {
      const val = breakdown[key] || 0;
      if (val <= 0) return;
      const barHeight = (val / yAxisMax) * chartHeight;
      const barY = currentBottom - barHeight;
      const [r, g, b] = colors[key];
      doc.setFillColor(r, g, b);
      doc.setDrawColor(255, 255, 255);
      doc.setLineWidth(0.2);
      doc.rect(barX, barY, bWidth, barHeight, 'FD');
      currentBottom = barY;
    });
    // Total label on top
    if (total > 0) {
      const topY = chartBottom - (total / yAxisMax) * chartHeight;
      doc.setFontSize(5.5);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(60, 60, 60);
      doc.text(total.toFixed(1), barX + bWidth / 2, topY - 2, { align: 'center' });
    }
  };

  materialNames.forEach((name, index) => {
    const groupCenter = chartLeft + index * groupWidth + groupWidth / 2;
    const oldBarX = groupCenter - barWidth - gap / 2;
    const curBarX = groupCenter + gap / 2;

    drawStackedBar(oldBarX, barWidth, values1990[name]?.breakdown || {}, colors1990);
    drawStackedBar(curBarX, barWidth, currentValues[name]?.breakdown || {}, currentColors);

    // X-axis label
    doc.setFontSize(6);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(80, 80, 80);
    const displayName = name.length > 20 ? name.substring(0, 18) + '...' : name;
    doc.text(displayName, groupCenter, chartBottom + 5, { align: 'center' });
  });

  // Legend — A1–A4 (same as /simulation UI)
  const legendY = chartBottom + 12;
  doc.setFontSize(7);
  let legendX = chartLeft;
  const legendItems = [
    { key: 'A1', label: 'A1 - Rohstoffbereitstellung' },
    { key: 'A2', label: 'A2 - Transport' },
    { key: 'A3', label: 'A3 - Produktion' },
    { key: 'A4', label: 'A4 - Transport Baustelle' },
  ];
  legendItems.forEach(({ key, label }) => {
    const [r, g, b_] = currentColors[key];
    doc.setFillColor(r, g, b_);
    doc.rect(legendX, legendY - 2.5, 4, 3, 'F');
    doc.setTextColor(80, 80, 80);
    doc.text(label, legendX + 5.5, legendY);
    legendX += doc.getTextWidth(label) + 10;
  });

  return legendY + 8;
};


const renderKPICardsInPDF = (doc, y, keyMetrics, co2Data, layerRows) => {
  const pageWidth = doc.internal.pageSize.getWidth();
  const cardWidth = (pageWidth - 28 - 12) / 4; // 4 cards, 4mm gap between
  const cardHeight = 42;
  const startX = 14;
  const gap = 4;

  // Calculate derived values (same logic as Analytics KeyMetrics)
  const totalEmissions = co2Data?.summary?.totalEmissions;
  const totalEmissions1990 = co2Data?.summary?.totalEmissions1990;

  let savings1990 = '';
  let savingsPct = '';
  let savingsEquivalent = '';

  if (totalEmissions && totalEmissions1990) {
    const currentSum = (totalEmissions.A1 || 0) + (totalEmissions.A2 || 0) + (totalEmissions.A3 || 0) + (totalEmissions.A4 || 0);
    const total1990Sum = (totalEmissions1990.A1 || 0) + (totalEmissions1990.A2 || 0) + (totalEmissions1990.A3 || 0) + (totalEmissions1990.A4 || 0);

    // Savings
    const savingsVal = total1990Sum - currentSum;
    savings1990 = `${savingsVal >= 0 ? '' : '-'}${Math.abs(savingsVal).toLocaleString('de-DE', { minimumFractionDigits: 1, maximumFractionDigits: 1 })} t CO2e`;

    // Percentage reduction vs. 1990
    const pctDiff = total1990Sum > 0 ? ((currentSum - total1990Sum) / total1990Sum) * 100 : 0;
    savingsPct = `${Math.abs(pctDiff).toFixed(1).replace('.', ',')} %`;

    // Savings equivalent
    const efMetric = keyMetrics.find(m => m.name === 'Emissionsfaktor');
    if (efMetric) {
      const efMatch = efMetric.value.match(/[\d,]+\.?\d*/);
      if (efMatch) {
        const efValue = parseFloat(efMatch[0].replace(',', '.'));
        if (efValue > 0 && savingsVal > 0) {
          const equiv = (savingsVal * 1000) / efValue;
          savingsEquivalent = `= ${equiv.toFixed(0).replace(/\B(?=(\d{3})+(?!\d))/g, '.')} t Asphaltmischgut`;
        }
      }
    }
  }

  const formatArea = (v) => (v || 0).toLocaleString('de-DE');
  const rows = layerRows || [];

  // Sanitize text for jsPDF (replace Unicode subscript ₂ with plain 2)
  const sanitize = (text) => (text || '').replace(/₂/g, '2');

  // Render text with CO₂ subscript by splitting at "CO2" and drawing a smaller shifted "2"
  const drawWithCO2Subscript = (rawText, tx, ty, fontSize, fontStyle) => {
    const text = (rawText || '').replace(/₂/g, '2');
    if (!text.includes('CO2')) {
      doc.setFontSize(fontSize);
      doc.setFont('helvetica', fontStyle);
      doc.text(text, tx, ty);
      return;
    }
    const parts = text.split('CO2');
    let curX = tx;
    parts.forEach((part, i) => {
      doc.setFontSize(fontSize);
      doc.setFont('helvetica', fontStyle);
      if (part) {
        doc.text(part, curX, ty);
        curX += doc.getTextWidth(part);
      }
      if (i < parts.length - 1) {
        doc.text('CO', curX, ty);
        curX += doc.getTextWidth('CO');
        doc.setFontSize(fontSize * 0.72);
        doc.text('2', curX, ty + fontSize * 0.13);
        curX += doc.getTextWidth('2');
      }
    });
    doc.setFontSize(fontSize);
    doc.setFont('helvetica', fontStyle);
  };

  // Format number with German formatting
  const formatGerman = (value) => {
    const safe = sanitize(value);
    const match = safe.match(/[\d,]+\.?\d*/);
    if (!match) return safe;
    const num = parseFloat(match[0].replace(/,/g, ''));
    if (isNaN(num)) return safe;
    const unit = safe.replace(match[0], '').trim();
    return `${num.toLocaleString('de-DE')} ${unit}`.trim();
  };

  // Reorder metrics: swap CO2e Emissionen and Emissionsfaktor
  const reordered = [...keyMetrics];
  const co2Idx = reordered.findIndex(m => m.name === 'CO₂e Emissionen');
  const efIdx = reordered.findIndex(m => m.name === 'Emissionsfaktor');
  if (co2Idx !== -1 && efIdx !== -1) {
    [reordered[co2Idx], reordered[efIdx]] = [reordered[efIdx], reordered[co2Idx]];
  }
  // Add savings card
  reordered.push({ name: 'Einsparungen gg. 1990', value: savings1990 || 'N/A', trend: savingsEquivalent });

  // Draw 4 cards
  reordered.forEach((metric, i) => {
    const x = startX + i * (cardWidth + gap);

    // Card background
    doc.setDrawColor(0, 0, 0);
    doc.setFillColor(255, 255, 255);
    doc.setLineWidth(0.3);
    doc.roundedRect(x, y, cardWidth, cardHeight, 2, 2, 'FD');

    // Card label
    doc.setFontSize(9);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(0, 0, 0);
    const cardLabel = metric.name === 'Emissionsfaktor' ? 'Emissionsfaktor A1-A4'
      : metric.name === 'CO₂e Emissionen' ? 'CO₂e Emissionen A1-A4'
      : metric.name;
    drawWithCO2Subscript(cardLabel, x + 3, y + 7, 9, 'normal');

    // Card value
    doc.setFontSize(14);
    doc.setFont('helvetica', 'bold');
    if (metric.name.startsWith('Einsparungen')) {
      doc.setTextColor(34, 139, 34);
      drawWithCO2Subscript(savingsPct || sanitize(metric.value), x + 3, y + 15, 14, 'bold');
      doc.setTextColor(0, 0, 0);
    } else {
      doc.setTextColor(0, 0, 0);
      const displayValue = formatGerman(metric.value);
      drawWithCO2Subscript(displayValue, x + 3, y + 15, 14, 'bold');
    }

    // Sub-info
    if (metric.name === 'Menge Asphaltmischgut') {
      doc.setFontSize(9);
      doc.setTextColor(0, 0, 0);
      const rowGap = rows.length > 3 ? 4.5 : 5;
      rows.forEach((row, j) => {
        const rowY = y + 22 + j * rowGap;
        doc.setFont('helvetica', 'bold');
        doc.text(row.label, x + 3, rowY);
        doc.setFont('helvetica', 'normal');
        doc.text(`${formatArea(row.area)} m²`, x + 15, rowY);
      });
    } else if (metric.name === 'Emissionsfaktor') {
      doc.setFontSize(9);
      doc.setTextColor(0, 0, 0);
      // Render "XX,X kg CO₂e/t" with manual subscript for the 2
      const drawEFValue = (v, tx, ty) => {
        doc.setFont('helvetica', 'normal');
        if (v === null || v === undefined) {
          doc.setFontSize(9);
          doc.text('—', tx, ty);
          return;
        }
        const num = v.toLocaleString('de-DE', { minimumFractionDigits: 1, maximumFractionDigits: 1 });
        doc.setFontSize(9);
        const prefix = `${num} kg CO`;
        doc.text(prefix, tx, ty);
        const prefixW = doc.getTextWidth(prefix);
        // subscript "2"
        doc.setFontSize(6.5);
        doc.text('2', tx + prefixW, ty + 1.2);
        const subW = doc.getTextWidth('2');
        // restore and finish
        doc.setFontSize(9);
        doc.text('e/t', tx + prefixW + subW, ty);
      };
      const rowGap = rows.length > 3 ? 4.5 : 5;
      rows.forEach((row, j) => {
        const rowY = y + 22 + j * rowGap;
        doc.setFontSize(9);
        doc.setFont('helvetica', 'bold');
        doc.text(row.label, x + 3, rowY);
        drawEFValue(row.ef, x + 15, rowY);
      });
    } else if (metric.name.startsWith('Einsparungen')) {
      doc.setFontSize(9);
      doc.setFont('helvetica', 'normal');
      doc.setTextColor(0, 0, 0);
      doc.text('entspricht', x + 3, y + 24);
      doc.setTextColor(34, 139, 34);
      drawWithCO2Subscript(savings1990, x + 3, y + 34, 14, 'bold');
    }
  });

  doc.setTextColor(0, 0, 0);
  return { y: y + cardHeight + 8, savingsPct, savings1990 };
};


// Load logo PNG as data URL for jsPDF
const loadLogoAsDataURL = () => {
  return new Promise((resolve) => {
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => {
      const canvas = document.createElement('canvas');
      canvas.width = img.naturalWidth;
      canvas.height = img.naturalHeight;
      const ctx = canvas.getContext('2d');
      ctx.drawImage(img, 0, 0);
      resolve(canvas.toDataURL('image/png'));
    };
    img.onerror = () => resolve(null);
    img.src = '/logos/Die_Autobahn_GmbH_des_Bundes_logo.svg.png';
  });
};

const generateProjectPDF = async (selectedProject, metrics, materialCO2Values) => {
  // Fetch saved calculations (inputs + breakdowns + 1990) for all materials from API
  const { inputsMap, breakdownMap, breakdown1990Map } = await fetchSavedCalculationsForMetrics(metrics, selectedProject.name);

  // Merge: prefer UI state (materialCO2Values) but fill in from API for materials not yet clicked
  const allCO2Values = { ...breakdownMap };
  Object.entries(materialCO2Values).forEach(([name, data]) => {
    allCO2Values[name] = data; // UI state overrides API data
  });

  // Fetch analytics data for KPI cards
  let keyMetrics = [];
  let co2Data = {};
  let layerAreas = {};
  try {
    const [metricsRes, co2Res, areasRes] = await Promise.all([
      analyticsService.getKeyMetrics(selectedProject.name),
      analyticsService.getCO2Breakdown(selectedProject.name),
      analyticsService.getLayerAreas(selectedProject.name)
    ]);
    if (metricsRes.success) keyMetrics = metricsRes.data || [];
    if (co2Res.success) co2Data = co2Res.data || {};
    if (areasRes.success) layerAreas = areasRes.data || {};
  } catch (e) {
    console.log('Could not fetch analytics data for PDF:', e.message);
  }

  // Load logo
  const logoDataURL = await loadLogoAsDataURL();

  const doc = new jsPDF();
  const pageWidth = doc.internal.pageSize.getWidth();
  let y = 20;

  // Title
  doc.setFontSize(18);
  doc.setFont('helvetica', 'bold');
  doc.text('CO2-Bilanzierung', 14, y);
  y += 8;
  doc.setFontSize(14);
  doc.setTextColor(34, 139, 34);
  doc.text(selectedProject.name, 14, y);
  doc.setTextColor(0, 0, 0);
  y += 10;

  // Timeframe
  if (selectedProject.timeframe) {
    doc.setFontSize(10);
    doc.setFont('helvetica', 'normal');
    doc.text(
      `Projektzeitraum: ${formatDate(selectedProject.timeframe.start)} - ${formatDate(selectedProject.timeframe.end)}`,
      14, y
    );
    y += 10;
  }

  // Build per-entry rows for the KPI cards.
  // DS / BS aggregate all entries; TS stays split so multiple TS layers appear individually.
  const materialEF = (name) => {
    const b = allCO2Values[name]?.breakdown || {};
    const ef = (b.A1 || 0) + (b.A2 || 0) + (b.A3 || 0) + (b.A4 || 0);
    return ef > 0 ? ef : null;
  };
  const weightedEF = (entries) => {
    let w = 0, mTot = 0;
    (entries || []).forEach((e) => {
      const ef = materialEF(e.material);
      const menge = Number(e.menge) || 0;
      if (ef == null || menge <= 0) return;
      w += ef * menge;
      mTot += menge;
    });
    return mTot > 0 ? w / mTot : null;
  };
  const ds = layerAreas?.deckschichten || [];
  const bs = layerAreas?.binderschichten || [];
  const ts = layerAreas?.tragschichten || [];
  const layerRows = [
    { label: 'DS', area: ds.reduce((s, e) => s + (e.flaeche || 0), 0), ef: weightedEF(ds) },
    { label: 'BS', area: bs.reduce((s, e) => s + (e.flaeche || 0), 0), ef: weightedEF(bs) },
    ...ts.map((e) => ({ label: 'TS', area: e.flaeche || 0, ef: materialEF(e.material) })),
  ];

  // KPI Cards
  let savingsPctStr = '';
  let savings1990Str = '';
  if (keyMetrics.length > 0) {
    const cardRes = renderKPICardsInPDF(doc, y, keyMetrics, co2Data, layerRows);
    y = cardRes.y;
    savingsPctStr = cardRes.savingsPct || '';
    savings1990Str = cardRes.savings1990 || '';
  }

  // LCA methodology section
  y += 6;
  y = checkPageBreak(doc, y, 50);
  doc.setFontSize(14);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(0, 0, 0);
  doc.text('Einsparungen gegenüber 1990', 14, y);
  y += 7;
  doc.setFontSize(9);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(0, 0, 0);
  const lcaText =
    'Die Lebenszyklusanalyse (LCA) zur Ermittlung des Treibhauspotenzials (Global Warming Potential, GWP) wurde ' +
    'gemäß den Berechnungsvorgaben der DIN EN 15804+A2 sowie der DIN EN 15643 durchgeführt. Die Systemgrenzen ' +
    'umfassen dabei die Module der Herstellungsphase der Baustoffe (A1\u2013A3) sowie der Errichtungsphase (A4).\n' +
    'Die Berechnung der CO2-Emissionen basiert auf der Zusammensetzung der jeweiligen Asphaltmischgüter, die ' +
    'aus den Erstprüfungen entnommen wurde. Für den Vergleich mit dem Referenzjahr 1990 wurden die jeweiligen ' +
    'Schichten auf Grundlage der zum damaligen Zeitpunkt gültigen Mischgutzusammensetzungen berechnet.\n' +
    'Die zugehörigen Angaben und daraus resultierenden Ergebnisse und sind im Folgenden detailliert dargestellt.';
  const textW = pageWidth - 28;
  lcaText.split('\n').forEach((para) => {
    const lines = doc.splitTextToSize(para, textW);
    lines.forEach((line, i) => {
      const words = line.trim().split(/\s+/);
      if (i === lines.length - 1 || words.length <= 1) {
        doc.text(line, 14, y);
      } else {
        const totalW = words.reduce((s, w) => s + doc.getTextWidth(w), 0);
        const gap = (textW - totalW) / (words.length - 1);
        let wx = 14;
        words.forEach((w) => { doc.text(w, wx, y); wx += doc.getTextWidth(w) + gap; });
      }
      y += 5;
    });
  });
  y += 2;
  // Bold sentence — justified, green highlights, CO2 subscript
  {
    const fs = 9;
    const subFs = 6.5;
    const lh = 5;
    const green = [34, 139, 34];
    const black = [0, 0, 0];
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(fs);
    // Extract numeric tonnage from savings string like "2.589,5 t CO2e"
    const tonsMatch = savings1990Str.match(/-?[\d.,]+/);
    const tonsStr = tonsMatch ? tonsMatch[0] : '0';
    const pctStr = savingsPctStr || '0 %';
    // Word atoms: indivisible units, each an array of styled parts
    const atoms = [
      [{ t: 'Gegenüber', g: false }],
      [{ t: 'dem', g: false }],
      [{ t: 'Referenzjahr', g: false }],
      [{ t: '1990', g: false }],
      [{ t: 'konnte', g: false }],
      [{ t: 'der', g: false }],
      [{ t: 'CO', g: false }, { t: '2', g: false, sub: true }, { t: '-Ausstoß', g: false }],
      [{ t: 'um', g: false }],
      [{ t: pctStr, g: true }],
      [{ t: 'reduziert', g: false }],
      [{ t: 'werden,', g: false }],
      [{ t: 'was', g: false }],
      [{ t: 'bei', g: false }],
      [{ t: 'diesem', g: false }],
      [{ t: 'Projekt', g: false }],
      [{ t: 'einer', g: false }],
      [{ t: 'Einsparung', g: false }],
      [{ t: 'von', g: false }],
      [{ t: `${tonsStr} Tonnen CO`, g: true }, { t: '2', g: true, sub: true }, { t: 'e', g: true }],
      [{ t: 'entspricht.', g: false }],
    ];
    const atomW = (atom) => atom.reduce((s, p) => {
      if (p.sub) { doc.setFontSize(subFs); const w = doc.getTextWidth(p.t); doc.setFontSize(fs); return s + w; }
      doc.setFontSize(fs);
      return s + doc.getTextWidth(p.t);
    }, 0);
    const spW = doc.getTextWidth(' ');
    // Greedy line-break
    const bLines = [];
    let cur = []; let curW = 0;
    atoms.forEach((atom) => {
      const aw = atomW(atom);
      const sp = cur.length > 0 ? spW : 0;
      if (curW + sp + aw > textW && cur.length > 0) { bLines.push(cur); cur = [atom]; curW = aw; }
      else { cur.push(atom); curW += sp + aw; }
    });
    if (cur.length > 0) bLines.push(cur);
    // Render justified
    bLines.forEach((line, li) => {
      const isLast = li === bLines.length - 1;
      const totalW = line.reduce((s, atom) => s + atomW(atom), 0);
      const gap = (isLast || line.length <= 1) ? spW : (textW - totalW) / (line.length - 1);
      let cx = 14;
      line.forEach((atom, ai) => {
        atom.forEach((part) => {
          const [r, g2, b] = part.g ? green : black;
          doc.setTextColor(r, g2, b);
          doc.setFont('helvetica', 'bold');
          if (part.sub) {
            doc.setFontSize(subFs);
            doc.text(part.t, cx, y + fs * 0.15);
            cx += doc.getTextWidth(part.t);
            doc.setFontSize(fs);
          } else {
            doc.setFontSize(fs);
            doc.text(part.t, cx, y);
            cx += doc.getTextWidth(part.t);
          }
        });
        if (ai < line.length - 1) cx += gap;
      });
      y += lh;
    });
    y += 4;
  }
  doc.setTextColor(0, 0, 0);
  doc.setFont('helvetica', 'normal');

  // 1990 comparison chart right after cards
  if (Object.keys(allCO2Values).length > 0 && Object.keys(breakdown1990Map).length > 0) {
    y += 10;
    y = checkPageBreak(doc, y, 110);
    doc.setFontSize(14);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(0, 0, 0);
    doc.text('1990 vs. Aktuell', 14, y);
    y += 8;

    y = renderComparisonBarChartInPDF(doc, y, allCO2Values, breakdown1990Map);

    // Page break after chart
    doc.addPage();
    y = 20;
  }

  // Minimal modern table renderer: no fills, thin separator lines, generous spacing
  const drawStyledTable = (startY, columns, rows) => {
    const leftMargin = 14;
    const tableW = pageWidth - 28;
    const headerH = 8;
    const rowH = 8;
    const cellPad = 2;
    const drawCell = (text, col, rowY, baseline) => {
      const str = String(text ?? '-');
      const tx = col.align === 'right' ? col.x + col.width - cellPad : col.x + cellPad;
      if (!str.includes('CO2')) {
        doc.text(str, tx, rowY + baseline, { align: col.align || 'left' });
        return;
      }
      // Render with subscript "2" after CO
      const parts = str.split('CO2');
      const mainSize = doc.getFontSize();
      const subSize = mainSize * 0.7;
      // Pre-compute total width for right alignment
      let totalW = 0;
      parts.forEach((p, i) => {
        totalW += doc.getTextWidth(p);
        if (i < parts.length - 1) {
          totalW += doc.getTextWidth('CO');
          doc.setFontSize(subSize);
          totalW += doc.getTextWidth('2');
          doc.setFontSize(mainSize);
        }
      });
      let cx = col.align === 'right' ? col.x + col.width - cellPad - totalW : col.x + cellPad;
      parts.forEach((p, i) => {
        if (p) {
          doc.text(p, cx, rowY + baseline);
          cx += doc.getTextWidth(p);
        }
        if (i < parts.length - 1) {
          doc.text('CO', cx, rowY + baseline);
          cx += doc.getTextWidth('CO');
          doc.setFontSize(subSize);
          doc.text('2', cx, rowY + baseline + 0.8);
          cx += doc.getTextWidth('2');
          doc.setFontSize(mainSize);
        }
      });
    };

    let yy = startY;

    // Header — bold black label, slightly larger than body
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(10);
    doc.setTextColor(0, 0, 0);
    columns.forEach((col) => drawCell(col.header, col, yy, 5.5));
    yy += headerH - 1;

    // Thin accent line under header
    doc.setDrawColor(34, 139, 34);
    doc.setLineWidth(0.5);
    doc.line(leftMargin, yy, leftMargin + tableW, yy);
    yy += 3;

    // Rows — clean text with a very light separator between them
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(10);
    doc.setTextColor(30, 30, 30);
    rows.forEach((row, i) => {
      yy = checkPageBreak(doc, yy);
      columns.forEach((col, ci) => {
        const isFirst = ci === 0;
        doc.setFont('helvetica', isFirst ? 'bold' : 'normal');
        doc.setTextColor(isFirst ? 30 : 60, isFirst ? 30 : 60, isFirst ? 30 : 60);
        drawCell(row[ci], col, yy, 5.5);
      });
      yy += rowH;
      if (i < rows.length - 1) {
        doc.setDrawColor(235, 235, 235);
        doc.setLineWidth(0.2);
        doc.line(leftMargin, yy - 1.5, leftMargin + tableW, yy - 1.5);
      }
    });

    doc.setTextColor(0, 0, 0);
    return yy + 2;
  };

  // Materials overview table
  doc.setFontSize(14);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(0, 0, 0);
  doc.text('Eingebaute Asphaltmischgüter', 14, y);
  y += 6;

  if (metrics.length === 0) {
    doc.setFontSize(10);
    doc.setFont('helvetica', 'normal');
    doc.text('Keine Materialien vorhanden.', 14, y);
    y += 10;
  } else {
    const cols1 = [
      { header: 'Material',    x: 14,  width: 100, align: 'left' },
      { header: 'Menge (t)',   x: 114, width: 40,  align: 'right' },
      { header: 'CO2e (kg/t)', x: 154, width: 42,  align: 'right' },
    ];
    const rows1 = metrics.map((metric) => {
      const co2Val = metric.co2Value ?? allCO2Values[metric.name]?.value;
      return [
        metric.name || '-',
        metric.value != null ? Number(metric.value).toLocaleString('de-DE') : '-',
        co2Val !== undefined ? co2Val.toFixed(2).replace('.', ',') : '-',
      ];
    });
    y = drawStyledTable(y, cols1, rows1);
  }

  y += 8;

  // CO2e Breakdown table
  if (Object.keys(allCO2Values).length > 0) {
    y = checkPageBreak(doc, y, 30);
    doc.setFontSize(14);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(0, 0, 0);
    doc.text('Detailaufschlüsselung', 14, y);
    y += 6;

    const has1990 = Object.keys(breakdown1990Map).length > 0;
    // Render subtitle with subscript "2" in CO2 — black text
    doc.setFontSize(8);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(0, 0, 0);
    {
      const prefix = 'Werte in kg CO';
      const suffix = has1990 ? 'e/t — Aktuell / 1990' : 'e/t';
      let sx = 14;
      doc.text(prefix, sx, y);
      sx += doc.getTextWidth(prefix);
      doc.setFontSize(6);
      doc.text('2', sx, y + 0.8);
      sx += doc.getTextWidth('2');
      doc.setFontSize(8);
      doc.text(suffix, sx, y);
    }
    y += 4;

    const cols2 = has1990 ? [
      { header: 'Material', x: 14,  width: 54, align: 'left' },
      { header: 'A1',       x: 68,  width: 32, align: 'right' },
      { header: 'A2',       x: 100, width: 32, align: 'right' },
      { header: 'A3',       x: 132, width: 32, align: 'right' },
      { header: 'A4',       x: 164, width: 32, align: 'right' },
    ] : [
      { header: 'Material',        x: 14,  width: 60, align: 'left' },
      { header: 'A1 (Rohstoff)',   x: 74,  width: 30, align: 'right' },
      { header: 'A2 (Transport)',  x: 104, width: 30, align: 'right' },
      { header: 'A3 (Produktion)', x: 134, width: 30, align: 'right' },
      { header: 'A4 (Baustelle)',  x: 164, width: 32, align: 'right' },
    ];
    const fmt = (n) => n.toFixed(2).replace('.', ',');
    const rows2 = Object.entries(allCO2Values).map(([name, data]) => {
      const b = data.breakdown || {};
      if (has1990) {
        const b90 = breakdown1990Map[name]?.breakdown || {};
        return [
          name,
          `${fmt(b.A1 || 0)} / ${fmt(b90.A1 || 0)}`,
          `${fmt(b.A2 || 0)} / ${fmt(b90.A2 || 0)}`,
          `${fmt(b.A3 || 0)} / ${fmt(b90.A3 || 0)}`,
          `${fmt(b.A4 || 0)} / ${fmt(b90.A4 || 0)}`,
        ];
      }
      return [name, fmt(b.A1 || 0), fmt(b.A2 || 0), fmt(b.A3 || 0), fmt(b.A4 || 0)];
    });
    y = drawStyledTable(y, cols2, rows2);
  }

  y += 6;

  // Calculator inputs per material
  const materialsWithInputs = metrics.filter(m => inputsMap[m.name]);
  if (materialsWithInputs.length > 0) {
    y += 6;
    doc.setDrawColor(0, 0, 0);
    y = checkPageBreak(doc, y, 20);
    doc.line(14, y, pageWidth - 14, y);
    y += 10;

    doc.setFontSize(14);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(0, 0, 0);
    doc.text('Berechnungseingaben', 14, y);
    y += 10;

    materialsWithInputs.forEach((metric) => {
      y = checkPageBreak(doc, y, 30);

      // Material sub-header
      doc.setFontSize(11);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(34, 139, 34);
      doc.text(metric.name, 16, y);
      doc.setTextColor(0, 0, 0);
      y += 7;

      y = renderCalculatorInputs(doc, y, inputsMap[metric.name]);
      y += 6;
    });
  }

  // Logo + Footer on every page
  const pageCount = doc.internal.getNumberOfPages();
  for (let i = 1; i <= pageCount; i++) {
    doc.setPage(i);
    // Logo top-right
    if (logoDataURL) {
      doc.addImage(logoDataURL, 'PNG', pageWidth - 44, 8, 32, 11);
    }
    // Footer
    doc.setFontSize(8);
    doc.setTextColor(0, 0, 0);
    doc.text(`Erstellt am: ${new Date().toLocaleDateString('de-DE')}`, 14, 290);
    doc.text('Die Autobahn GmbH des Bundes', pageWidth - 14 - doc.getTextWidth('Die Autobahn GmbH des Bundes'), 290);
    doc.text(`Seite ${i} / ${pageCount}`, pageWidth / 2 - 10, 290);
  }

  doc.save(`CO2-Bilanzierung_${selectedProject.name}.pdf`);
};

const SimulationHeader = ({ selectedProject, onOpenModal, metrics, materialCO2Values }) => {
  const [isGeneratingPDF, setIsGeneratingPDF] = useState(false);

  const handlePDFExport = async () => {
    setIsGeneratingPDF(true);
    try {
      await generateProjectPDF(selectedProject, metrics || [], materialCO2Values || {});
    } catch (error) {
      console.error('Error generating PDF:', error);
    } finally {
      setIsGeneratingPDF(false);
    }
  };

  return (
    <div className="flex justify-between items-center mb-8">
      <div className="flex items-center gap-3">
        <div>
          <h1 className="text-2xl">
            CO2-Bilanzierung{" "}
            <button
              onClick={onOpenModal}
              className="font-semibold text-green-500 hover:text-green-600 transition-colors focus:outline-none"
            >
              {selectedProject?.name || "Auswählen"}
            </button>
          </h1>
          {selectedProject?.timeframe && (
            <p className="text-gray-500">
              Projektzeitraum: {formatDate(selectedProject.timeframe.start)} - {formatDate(selectedProject.timeframe.end)}
            </p>
          )}
        </div>
        {selectedProject?.name && (
          <button
            onClick={handlePDFExport}
            disabled={isGeneratingPDF}
            className="p-2 hover:bg-gray-100 rounded-full transition-colors disabled:opacity-50"
            title="PDF exportieren"
          >
            {isGeneratingPDF ? (
              <Loader2 className="h-5 w-5 text-green-500 animate-spin" />
            ) : (
              <FileDown className="h-5 w-5 text-gray-500 hover:text-green-600" />
            )}
          </button>
        )}
      </div>
      <div className="flex items-center space-x-4">
        <button className="p-2 hover:bg-gray-100 rounded-full transition-colors">
        </button>
        <div className="flex items-center space-x-2">
          <img
            src="/logos/UserLogo.png"
            alt="Profile"
            className="h-8 w-8 rounded-full"
          />
          <span className="text-gray-700 font-medium">Autobahn GmbH</span>
        </div>
      </div>
    </div>
  );
};
export const Simulation = () => {
  const { isExpanded } = useSidebar();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedProject, setSelectedProject] = useState(null);
  const [metrics, setMetrics] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [selectedMaterial, setSelectedMaterial] = useState(null);
  const [materialCO2Values, setMaterialCO2Values] = useState({});
  const [materialCO2Loading, setMaterialCO2Loading] = useState(false);
  const [calculatorTransferFunction, setCalculatorTransferFunction] = useState(null);
  const [metricsRefreshKey, setMetricsRefreshKey] = useState(0);

  // Handle material specification data transfer
  const handleMaterialSpecificationTransfer = (values) => {
    if (calculatorTransferFunction) {
      calculatorTransferFunction(values);
    }
  };

  // Triggered after "Werte übernehmen" successfully writes Rohdichte → dichte
  // in constructionLayerAreas. Re-runs metric calc so "Eingebaute Asphaltmischgüter"
  // reflects the recalculated Menge.
  const handleMaterialDichteApplied = () => {
    setMetricsRefreshKey(k => k + 1);
  };

  // Callback to receive the transfer function from Calculator
  const handleCalculatorTransferFunctionReady = (transferFunction) => {
    setCalculatorTransferFunction(() => transferFunction);
  };

  const calculateMetrics = async (deliveryNotes, projectId) => {
    // Check quantity source setting for this project
    let quantitySource = 'deliveryNotes'; // default
    try {
      const quantitySourceResponse = await simulationService.getQuantitySource(projectId);
      if (quantitySourceResponse.success) {
        quantitySource = quantitySourceResponse.data.quantitySource;
      }
    } catch (error) {
      console.log('No quantity source setting found, using delivery notes');
    }

    if (quantitySource === 'calculatedLayers') {
      // Use calculated layer quantities
      return await calculateMetricsFromLayers(projectId);
    } else {
      // Use delivery note quantities (original logic)
      return calculateMetricsFromDeliveryNotes(deliveryNotes);
    }
  };

  const calculateMetricsFromDeliveryNotes = (deliveryNotes) => {
    if (!deliveryNotes || deliveryNotes.length === 0) return [];

    // Group by material and location
    const groupedNotes = _.groupBy(deliveryNotes, note => {
      const material = note.standard?.material?.asphaltMixture;
      const location = note.standard?.productionPlant?.location;
      const locationString = location ? `${location.postalCode} ${location.city}` : '';
      return `${material}__${locationString}`;
    });

    const materialMetrics = Object.entries(groupedNotes)
      .filter(([key]) => {
        const [material, location] = key.split('__');
        return material && location && material !== 'undefined' && location !== 'undefined';
      })
      .map(([key, notes]) => {
        const [material, location] = key.split('__');
        const totalQuantity = _.sumBy(notes,
          note => note.standard?.material?.quantities?.netto?.value || 0
        );

        // Get the first note's data to access location details
        const firstNote = notes[0];
        const productionPlant = firstNote?.standard?.productionPlant;

        // Check if there's a saved CO2 value in the note
        const co2Value = firstNote?.standard?.material?.co2Value;

        return {
          name: material,
          value: totalQuantity,
          location,
          productionPlant: {
            location: productionPlant?.location || {}
          },
          co2Value: co2Value
        };
      });

    return materialMetrics;
  };

  const calculateMetricsFromLayers = async (projectId) => {
    try {
      const layerAreasResponse = await simulationService.getLayerAreas(projectId);

      if (!layerAreasResponse.success || !layerAreasResponse.data) {
        console.log('No layer areas found for calculated quantities');
        return [];
      }

      const layerAreas = layerAreasResponse.data;
      const metrics = [];

      // Build flat list of layer entries with their type info
      const layerGroups = [
        { typeName: 'Deckschicht', typeKey: 'deckschichten', entries: layerAreas.deckschichten || [] },
        { typeName: 'Binderschicht', typeKey: 'binderschichten', entries: layerAreas.binderschichten || [] },
        { typeName: 'Tragschicht', typeKey: 'tragschichten', entries: layerAreas.tragschichten || [] }
      ];

      for (const group of layerGroups) {
        for (let idx = 0; idx < group.entries.length; idx++) {
          const entry = group.entries[idx];
          if (!entry.material || !(entry.menge > 0)) continue;

          let co2Value = undefined;
          try {
            const co2Response = await simulationService.getLayerCO2(projectId, group.typeKey, idx);
            if (co2Response.success && co2Response.data?.co2Data?.co2Value) {
              co2Value = co2Response.data.co2Data.co2Value;
            }
          } catch (error) {
            console.log(`No CO2 data found for ${group.typeName} #${idx}:`, error.message);
          }

          metrics.push({
            name: entry.material,
            value: entry.menge,
            location: '',
            productionPlant: { location: {} },
            co2Value,
            source: 'calculatedLayers',
            layerName: group.entries.length > 1
              ? `${group.typeName} ${idx + 1}`
              : group.typeName
          });
        }
      }

      return metrics;
    } catch (error) {
      console.error('Error calculating metrics from layers:', error);
      return [];
    }
  };

  // Function to fetch CO2 values for all materials
  const fetchCO2ValuesForMaterials = async (materials, projectId) => {
    if (!materials || !projectId) return;

    setMaterialCO2Loading(true);
    console.log('Fetching CO2 values for materials:', materials.map(m => m.name));

    try {
      const updatedMaterials = [...materials];

      // First, use the CO2 values that are already in the delivery notes
      // This reduces the need for additional API calls
      const materialsWithoutCO2 = [];

      updatedMaterials.forEach((material, index) => {
        // If the material already has a CO2 value from the delivery note, use it
        if (material.co2Value !== undefined) {
          console.log(`Using existing CO2 value for ${material.name}: ${material.co2Value}`);
        } else {
          // Otherwise, add it to the list of materials that need to fetch CO2 values
          materialsWithoutCO2.push({ material, index });
        }
      });

      // Only make API calls for materials that don't already have CO2 values
      if (materialsWithoutCO2.length > 0) {
        console.log(`Fetching CO2 values for ${materialsWithoutCO2.length} materials without existing values`);

        // Process materials in parallel with Promise.all, but limit to 3 concurrent requests
        // to avoid overwhelming the server
        const batchSize = 3;
        for (let i = 0; i < materialsWithoutCO2.length; i += batchSize) {
          const batch = materialsWithoutCO2.slice(i, i + batchSize);

          const batchResults = await Promise.all(
            batch.map(async ({ material, index }) => {
              if (!material.productionPlant?.location?.postalCode ||
                  !material.productionPlant?.location?.city) {
                return { index, co2Value: undefined };
              }

              try {
                const result = await simulationService.getLastCalculation(
                  projectId,
                  material.name,
                  {
                    postalCode: material.productionPlant.location.postalCode,
                    city: material.productionPlant.location.city
                  }
                );

                if (result.success && result.data && result.data.co2Value !== undefined) {
                  console.log(`Found CO2 value for ${material.name}:`, result.data.co2Value);
                  return { index, co2Value: result.data.co2Value };
                }
              } catch (error) {
                // Ignore 404 errors - they're expected when no calculation exists
                if (error.response && error.response.status === 404) {
                  console.log(`No saved calculation found for ${material.name}`);
                } else {
                  console.error(`Error fetching CO2 value for ${material.name}:`, error);
                }
              }

              return { index, co2Value: undefined };
            })
          );

          // Update materials with fetched CO2 values
          batchResults.forEach(result => {
            if (result && result.co2Value !== undefined) {
              updatedMaterials[result.index].co2Value = result.co2Value;
            }
          });
        }
      }

      console.log('Updated materials with CO2 values:', updatedMaterials);
      setMetrics(updatedMaterials);

      // We'll only keep existing chart data, not create default breakdowns
      const newChartData = {};
      updatedMaterials.forEach(material => {
        // Only keep existing breakdown data if we already have it
        if (materialCO2Values[material.name]) {
          newChartData[material.name] = materialCO2Values[material.name];
        }
        // We no longer create default breakdowns with all value in A1
      });

      // Update chart data if we have existing values to keep
      if (Object.keys(newChartData).length > 0) {
        setMaterialCO2Values(prev => ({
          ...prev,
          ...newChartData
        }));
      }
    } catch (error) {
      console.error('Error fetching CO2 values:', error);
    } finally {
      setMaterialCO2Loading(false);
    }
  };

  const handleMaterialClick = (material) => {
    if (selectedProject?.name) {
      setSelectedMaterial({
        name: material.name,
        value: material.value,
        productionPlant: material.productionPlant,
        projectId: selectedProject.name,
        source: material.source, // Pass the source (calculatedLayers or deliveryNotes)
        layerName: material.layerName // Pass the layer name for calculated layers
      });
    }
  };

  useEffect(() => {
    const loadMetrics = async () => {
      if (selectedProject?.data && selectedProject?.name) {
        setIsLoading(true);
        try {
          const calculatedMetrics = await calculateMetrics(selectedProject.data, selectedProject.name);
          setMetrics(calculatedMetrics);

          // Fetch CO2 values for all materials
          if (calculatedMetrics.length > 0) {
            fetchCO2ValuesForMaterials(calculatedMetrics, selectedProject.name);
          }
        } catch (error) {
          console.error('Error loading metrics:', error);
          setMetrics([]);
        } finally {
          setIsLoading(false);
        }
      }
    };

    loadMetrics();
  }, [selectedProject, metricsRefreshKey]);

  const handleCalculatorSave = (co2Value, breakdown, materialName) => {
    // Update the chart data
    setMaterialCO2Values(prev => ({
      ...prev,
      [materialName]: {
        value: co2Value,
        breakdown: breakdown
      }
    }));

    // Also update the CO2 value in the metrics for the material cards
    setMetrics(prev =>
      prev.map(metric =>
        metric.name === materialName
          ? { ...metric, co2Value: co2Value }
          : metric
      )
    );
  };

  const handlePermanentSave = async (materialName, co2Value, breakdown) => {
    console.log(`Permanent save for ${materialName} with CO2 value: ${co2Value}`);

    // Update the chart data
    setMaterialCO2Values(prev => ({
      ...prev,
      [materialName]: {
        value: co2Value,
        breakdown: breakdown
      }
    }));

    // Update the CO2 value in the metrics for the material cards
    setMetrics(prev =>
      prev.map(metric =>
        metric.name === materialName
          ? { ...metric, co2Value: co2Value }
          : metric
      )
    );

    // No need to refresh all material data - we already have the updated value
    // This prevents excessive API calls and potential race conditions
  };

  const handleRemoveMaterial = (materialName) => {
    setMaterialCO2Values(prev => {
      const newValues = { ...prev };
      delete newValues[materialName];
      return newValues;
    });
  };

  const handleSelectProject = (project) => {
    // Clear any existing material CO2 values when selecting a new project
    setMaterialCO2Values({});

    setSelectedProject({
      ...project,
      projectId: project.name
    });
  };

  const leftMargin = isExpanded ? 'ml-64' : 'ml-16';

  return (
    <div className={`${leftMargin} min-h-screen bg-gray-50 transition-all duration-300`}>
      <div className="p-8">
        <SimulationHeader
          selectedProject={selectedProject}
          onOpenModal={() => setIsModalOpen(true)}
          metrics={metrics}
          materialCO2Values={materialCO2Values}
        />

        <div className="mt-8 flex gap-8 h-[calc(100vh-10rem)]">
          {/* Main content container with fixed height */}
          <div className="flex w-full h-full gap-8">
            {/* Left column - Calculator */}
            <div className="w-1/3 h-full">
              <Card className="h-full">
                <Calculator
                  selectedMaterial={selectedMaterial}
                  projectId={selectedProject?.name}
                  onSave={handleCalculatorSave}
                  onPermanentSave={handlePermanentSave}
                  onMaterialSpecificationTransfer={handleCalculatorTransferFunctionReady}
                />
              </Card>
            </div>

            {/* Right column - Material cards and graph */}
            <div className="w-2/3 flex flex-col h-full">
              {/* Material cards section - fixed height */}
              <div className="flex-none mb-4" style={{ height: '200px' }}>
                <div className="flex justify-between items-center mb-2">
                  <h3 className="text-lg font-semibold">Eingebaute Asphaltmischgüter</h3>
                  {metrics.length > 2 && (
                    <div className="text-sm text-gray-500">
                      Scrollen Sie horizontal für mehr Asphaltmischgüter →
                    </div>
                  )}
                </div>
                <div className="overflow-x-auto overflow-y-hidden pb-2 scrollbar-visible" style={{ overflowX: 'auto', WebkitOverflowScrolling: 'touch', height: 'calc(100% - 30px)' }}>
                  <div style={{ width: metrics.length > 2 ? 'max-content' : '100%', minWidth: '100%', display: 'flex', gap: '1rem' }}>
                    <KeyMetrics
                      metrics={metrics}
                      isLoading={isLoading || materialCO2Loading}
                      onMetricClick={handleMaterialClick}
                      maxRows={2}
                      selectedProject={selectedProject}
                      onTransferValues={handleMaterialSpecificationTransfer}
                      onMaterialDichteApplied={handleMaterialDichteApplied}
                    />
                  </div>
                </div>
              </div>

              {/* Graph container - fixed height calculation */}
              {metrics.length > 0 ? (
                <div className="flex-1" style={{ height: 'calc(100% - 200px - 1rem)' }}>
                  <Card className="h-full">
                    <MaterialChart
                      materialCO2Values={materialCO2Values}
                      onRemoveMaterial={handleRemoveMaterial}
                    />
                  </Card>
                </div>
              ) : (
                <div className="flex-1 flex items-center justify-center" style={{ height: 'calc(100% - 200px - 1rem)' }}>
                  <div className="text-gray-400 text-center">
                    <p className="text-green-500 font-semibold">Auswählen</p>
                    <p className="text-sm mt-2">Wählen Sie ein Projekt und Asphaltmischgut aus, um mit dem CO2e-Vergleich zu starten</p>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        <ProjectSearchModal
          isOpen={isModalOpen}
          onClose={() => setIsModalOpen(false)}
          onSelectProject={handleSelectProject}
        />
      </div>
    </div>
  );
};

export default Simulation;